import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

export const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "danger-outline" | "white";
export type ButtonSize = "md" | "sm";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-primary text-text-on-primary hover:bg-primary-pressed",
  secondary: "border-2 border-text-primary text-text-primary hover:bg-text-primary hover:text-surface-background",
  ghost: "bg-primary-soft text-text-primary hover:bg-surface-hero",
  danger: "bg-danger-text text-text-on-primary hover:opacity-90",
  "danger-outline": "border-2 border-danger-text text-danger-text hover:bg-danger-soft",
  white: "bg-surface-card text-text-primary hover:bg-primary-soft",
};

/** Class list for anything that should look like a button (links included): uppercase pill, web theme. */
export function buttonClass(variant: ButtonVariant = "primary", size: ButtonSize = "md", className?: string) {
  return cx(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill font-body font-semibold uppercase tracking-wide transition-colors",
    // Small buttons stay compact for a mouse but grow to a 44px target on touch screens.
    size === "md" ? "min-h-11 px-6 text-small" : "min-h-9 px-4 text-caption pointer-coarse:min-h-11",
    VARIANTS[variant],
    // Disabled reads as unavailable, not faded: muted text on the subtle surface (≥4.5:1), no hover change.
    "disabled:cursor-not-allowed disabled:border-transparent disabled:bg-surface-subtle disabled:text-text-muted",
    className,
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize; loading?: boolean };

/** Pill button. `loading` keeps the label and marks the button busy. */
export function Button({ variant = "primary", size = "md", loading = false, className, type = "button", disabled, children, ...props }: ButtonProps) {
  return (
    <button type={type} className={buttonClass(variant, size, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      {loading && <span aria-hidden="true" className="size-4 animate-spin rounded-full border-2 border-current border-r-transparent" />}
      {children}
    </button>
  );
}

/** Filter and choice chips (programmes, exams, statuses): one look everywhere; dark fill when selected. */
export function chipClass(selected: boolean, className?: string) {
  return cx(
    "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-pill px-4 text-caption font-semibold transition-colors pointer-coarse:min-h-11",
    selected ? "bg-text-primary text-surface-background" : "bg-surface-card text-text-primary ring-1 ring-border-strong hover:bg-primary-soft",
    className,
  );
}

/**
 * On/off switch (role="switch"). The 28px track sits in a 44px-tall hit area; the off track uses the control border
 * colour so it stays visible (3:1) against cards and backgrounds.
 */
export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (next: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="group inline-flex min-h-11 shrink-0 items-center disabled:cursor-not-allowed"
    >
      <span
        aria-hidden="true"
        className={cx(
          "relative inline-flex h-7 w-12 items-center rounded-pill transition-colors group-disabled:opacity-50",
          checked ? "bg-text-primary" : "bg-border-control",
        )}
      >
        <span className={cx("inline-block size-5 rounded-pill bg-surface-card shadow-card transition-transform", checked ? "translate-x-6" : "translate-x-1")} />
      </span>
    </button>
  );
}

/** Card: soft cream panel with a hairline ring, radius ≥ 20px (spec §2.1). */
export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("rounded-card bg-surface-card p-6 shadow-card", className)} {...props} />;
}

/** Chip / pill used for exams, programmes and filters. */
export function Pill({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx("inline-flex items-center gap-1 rounded-pill bg-surface-card px-3 py-1 text-caption font-semibold text-text-primary shadow-card", className)}>{children}</span>;
}

/** Orange uppercase label above a section heading. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx("text-caption font-bold uppercase tracking-widest text-text-link", className)}>{children}</p>;
}
